import { NextPage } from 'next';
import { DefaultSeo } from 'next-seo';
import { AppProps } from 'next/app';
import { QueryClient, QueryClientProvider } from 'react-query';
import { RecoilEnv, RecoilRoot } from 'recoil';
import { MyCustomTheme } from 'themes';

import { getBasicTokenString } from '@helpers/credentials';
import { AuthProvider } from 'providers/useAuth';

import { ChakraProvider } from '@chakra-ui/provider';
import { createStandaloneToast } from '@chakra-ui/toast';
import NextProgress from 'next-progress';
import seoConfig from '../next-seo.config';

const { ToastContainer } = createStandaloneToast();

import ThemeColor from '@components/ThemeColor';
import { initPWAInstall } from '@helpers/installapp';
import fonts from '@libraries/fonts';
import { AcademicProvider } from '@providers/useAcademic';
import Head from 'next/head';
import { useEffect } from 'react';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      suspense: false,
    },
    mutations: {
      retry: false,
    },
  },
});
RecoilEnv.RECOIL_DUPLICATE_ATOM_KEY_CHECKING_ENABLED = false;

interface CustomAppProps extends AppProps {
  Component: NextPage;
}

const App: NextPage<CustomAppProps> = ({ Component, pageProps }) => {
  const token = getBasicTokenString();
  initPWAInstall();

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker
          .register('/sw.js')
          .then((registration) => {
            console.log(
              'Service Worker registered with scope:',
              registration.scope,
            );
          })
          .catch((error) => {
            console.error('Service Worker registration failed:', error);
          });
      });
    }
  }, []);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Head>
      <style jsx global>
        {`
          :root {
            --font-heading: ${fonts.fontHeading.style.fontFamily};
            --font-body: ${fonts.fontBody.style.fontFamily};
          }
        `}
      </style>
      <DefaultSeo {...seoConfig} />
      <RecoilRoot>
        <ChakraProvider theme={MyCustomTheme}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider token={token} initialProfile={pageProps?.profile}>
              <AcademicProvider token={token}>
                <ThemeColor />
                <Component {...pageProps} />
                <ToastContainer />
              </AcademicProvider>
            </AuthProvider>
          </QueryClientProvider>
          <NextProgress
            options={{ showSpinner: true }}
            color="var(--chakra-colors-button-primary)"
          />
        </ChakraProvider>
      </RecoilRoot>
    </>
  );
};

export default App;
